import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useRef, useEffect } from "react";
import "./carouselSlider.css";

import {
  faChevronRight,
  faChevronLeft,
} from "@fortawesome/free-solid-svg-icons";

export default function CarouselSlider({
  children,
  fixedWidth,
  gap,
  arrowGap,
  stepless,
  resetOnChange,
  responsive,
}) {
  const [currentPage, setCurrentPage] = useState(0);

  const [offset, setOffset] = useState(0);

  const [panning, setPanning] = useState({
    isDown: false,
    startPos: null,
  });

  useEffect(() => {
    setCurrentPage(0);
    setOffset(0);
    setPanning({
      isDown: false,
      startPos: null,
    });
  }, [resetOnChange]);

  let pageWidth = 25;
  let pageNum = 4;

  if (responsive) {
    const pageW = window.screen.width;
    if (pageW < 1024) {
      pageWidth = 50;
      pageNum = 2;
    } else if (pageW < 1440) {
      pageWidth = 33;
      pageNum = 3;
    }
  }

  let lastPage = children.length - pageNum;

  if (lastPage < 0) {
    lastPage = 0;
  }

  const listRef = useRef();

  const moveStyle = {
    transform: `translateX(calc(${currentPage * pageWidth}% + ${offset}px))`,
  };

  const elements = children.map((val, idx) => {
    return (
      <div
        className="slider-element-container"
        key={idx}
        style={{
          width: fixedWidth ? `${pageWidth}%` : "auto",
          padding: `0 ${gap ? gap : "15px"}`,
        }}
      >
        {val}
      </div>
    );
  });

  function goNext() {
    if (reachedEnd()) return;

    if (stepless) {
      setOffset(listRef.current.scrollWidth - listRef.current.clientWidth);
      return;
    }

    setCurrentPage((val) => val + 1);
  }

  function goPrev() {
    if (reachedStart()) return;

    if (stepless) {
      setOffset(0);
      return;
    }

    setCurrentPage((val) => val - 1);
  }

  function handleTouchDown(e) {
    setPanning({
      isDown: true,
      startPos: e.touches[0].pageX,
    });
  }

  function handleMouseDown(e) {
    e.preventDefault();
    setPanning({
      isDown: true,
      startPos: e.pageX,
    });
  }

  function handleTouchMove(e) {
    if (!panning.isDown) return;
    const distance = e.touches[0].pageX - panning.startPos;
    listRef.current.style.transition = "0s";
    listRef.current.style.transform = `translateX(calc(${
      currentPage * pageWidth
    }% + ${offset + distance}px))`;
  }

  function handleMouseMove(e) {
    e.preventDefault();
    if (!panning.isDown) return;
    const distance = e.pageX - panning.startPos;
    listRef.current.style.transition = "0s";
    listRef.current.style.transform = `translateX(calc(${
      currentPage * pageWidth
    }% + ${offset + distance}px))`;
  }

  function handleTouchSnap(e) {
    if (!panning.isDown) return;

    const distance = e.changedTouches[0].pageX - panning.startPos;
    setPanning({
      isDown: false,
      startPos: null,
    });

    const width = listRef.current.clientWidth;
    if (stepless) {
      let newOffset = offset + distance;

      if (newOffset < 0) {
        newOffset = 0;
      }

      if (newOffset > listRef.current.scrollWidth - width) {
        newOffset = listRef.current.scrollWidth - width;
      }
      setOffset(newOffset);
      listRef.current.style.transform = `translateX(${offset}px)`;
      listRef.current.style.transition = "500ms ease-in-out";
      return;
    }
    const relativeDistance = distance / width;

    const newLocation = Math.round(relativeDistance / (pageWidth / 100));

    let newPage = currentPage + newLocation;

    if (newPage < 0) {
      newPage = 0;
    }

    if (newPage > lastPage) {
      newPage = lastPage;
    }

    setCurrentPage(newPage);

    listRef.current.style.transform = `translateX(${currentPage * pageWidth}%)`;

    listRef.current.style.transition = "500ms ease-in-out";
  }

  function handleSnap(e) {
    e.preventDefault();

    if (!panning.isDown) return;

    const distance = e.pageX - panning.startPos;
    setPanning({
      isDown: false,
      startPos: null,
    });

    const width = listRef.current.clientWidth;
    if (stepless) {
      let newOffset = offset + distance;

      if (newOffset < 0) {
        newOffset = 0;
      }

      if (newOffset > listRef.current.scrollWidth - width) {
        newOffset = listRef.current.scrollWidth - width;
      }
      setOffset(newOffset);
      listRef.current.style.transform = `translateX(${offset}px)`;
      listRef.current.style.transition = "500ms ease-in-out";
      return;
    }

    const relativeDistance = distance / width;

    const newLocation = Math.round(relativeDistance / (pageWidth / 100));

    let newPage = currentPage + newLocation;

    if (newPage < 0) {
      newPage = 0;
    }

    if (newPage > lastPage) {
      newPage = lastPage;
    }

    setCurrentPage(newPage);

    listRef.current.style.transform = `translateX(${currentPage * pageWidth}%)`;

    listRef.current.style.transition = "500ms ease-in-out";
  }

  function reachedEnd() {
    if (currentPage === lastPage) {
      return true;
    } else if (
      listRef.current &&
      offset === listRef.current.scrollWidth - listRef.current.clientWidth
    ) {
      return true;
    } else return false;
  }

  function reachedStart() {
    if (currentPage === 0 && offset === 0) return true;
    else return false;
  }

  return (
    <div
      className="slider-container"
      style={{
        columnGap: arrowGap ? arrowGap : "20px",
      }}
    >
      <span
        className={`prev-arrow ${reachedStart() ? "disabled" : ""}`}
        onClick={goPrev}
      >
        <FontAwesomeIcon icon={faChevronRight} />
      </span>
      <div className="main-slider">
        <div
          className="main-list"
          style={moveStyle}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleSnap}
          onMouseLeave={handleSnap}
          onTouchStart={handleTouchDown}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchSnap}
          onTouchCancel={handleTouchSnap}
          ref={listRef}
          draggable={true}
        >
          {elements}
        </div>
      </div>
      <span
        className={`next-arrow ${reachedEnd() ? "disabled" : ""}`}
        onClick={goNext}
      >
        <FontAwesomeIcon icon={faChevronLeft} />
      </span>
    </div>
  );
}
